<script setup lang="ts">
import type { DataItem as AirRoadJobsProductsCustomTableDataItem } from '@/components/customTables/AirRoadJobsProductsCustomTable.vue';
import type { ListItem } from '@/components/descLists/StatusDescList.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { getARServiceData } from '@/utils/Common';
import AirRoadJobsProductsCustomTable from '@/components/customTables/AirRoadJobsProductsCustomTable.vue';
import StatusDescList from '@/components/descLists/StatusDescList.vue';
import ThinJobStopDiagram from '@/components/diagrams/ThinJobStopDiagram.vue';
import * as commonConstants from '@/constants/common';

export interface Props {
  specialOptions?: openapi.AirroadJobOpenapi['special_options'] | null;
  stops?: openapi.AirroadJobOpenapi['stops'];
  list1?: ListItem[];
  list2?: ListItem[];
  airRoadJobsProductsTableData?: AirRoadJobsProductsCustomTableDataItem[];
  airRoadJobsProductsTableTotalData?: {
    volume?: string;
    weight?: string;
  };
}
export type TableFoldProps = Props;
const props = withDefaults(defineProps<Props>(), {
  specialOptions: undefined,
  stops: () => [],
  list1: () => [],
  list2: () => [],
  airRoadJobsProductsTableData: () => [],
  airRoadJobsProductsTableTotalData: undefined,
});
</script>

<template>
  <section
    data-testid="airRoadJobsTableFold"
    :class="$style.airRoadJobsTableFold"
  >
    <div :class="$style.left">
      <div :class="$style.listsItem">
        <div>
          <StatusDescList :class="$style.statusDescList" :list="props.list1" />
        </div>
        <div>
          <StatusDescList :class="$style.statusDescList" :list="props.list2" />
        </div>
      </div>
      <div :class="$style.diagramItem">
        <ThinJobStopDiagram :stops="props.stops" />
        <div :class="$style.addresses">
          <div v-for="(stop, stopIndex) in props.stops" :key="stopIndex">
            <!-- almost the same in JobDiagramDescList.vue -->
            {{ stop.address_line_1 }}
            {{ stop.address_line_2
            }}<template v-if="stop.address_line_1 || stop.address_line_2"
              >,</template
            >
            {{ stop.suburb }}<template v-if="stop.suburb">,</template>
            {{ stop.state }}<template v-if="stop.state">,</template>
            {{ stop.postcode }}
          </div>
        </div>
      </div>
      <div v-if="props.specialOptions?.length" :class="$style.specialOptions">
        <h4 :class="$style.h4">Special options:</h4>
        <div
          v-for="specialOptionItem in props.specialOptions"
          :key="specialOptionItem.specialOptionId"
          class="flex"
        >
          <div>
            {{ specialOptionItem.isPickup ? 'Pickup' : 'Delivery' }}
            {{ commonConstants.EM_DASH }}
          </div>
          <div
            v-if="!!getARServiceData(specialOptionItem.specialOptionId)"
            class="ml-2"
          >
            <span class="font-semibold">
              {{ getARServiceData(specialOptionItem.specialOptionId)?.code }}
            </span>
            ({{ getARServiceData(specialOptionItem.specialOptionId)?.name }})
          </div>
          <div v-if="specialOptionItem.notes" class="ml-2">
            {{ commonConstants.EM_DASH }} {{ specialOptionItem.notes }}
          </div>
        </div>
      </div>
    </div>
    <div :class="$style.right">
      <h4 :class="$style.h4">Products:</h4>
      <AirRoadJobsProductsCustomTable
        :data="airRoadJobsProductsTableData"
        :total-data="airRoadJobsProductsTableTotalData"
      />
    </div>
  </section>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.airRoadJobsTableFold {
  display: flex;
  gap: 3rem;
}
  .h4 {
    font-weight: 500;
    margin-top: 0;
    margin-bottom: 10px;
  }
  .left {
    width: 100%;
  }
    .listsItem {
      display: flex;
      gap: 3rem;
      font-size: 14px;
    }
      .listsItem > div {
      }
    .diagramItem {
      margin-top: 2rem;
    }
      .addresses {
        display: flex;
        padding-left: 70px;
        font-size: 14px;

        & > div {
          width: 100%;
        }
      }
    .specialOptions {
      margin-top: 1.5rem;
    }
  .right {
    width: calc(100% / 3);
    flex-shrink: 0;
  }
</style>
